import React from 'react';
import BigCalendar, { View as BigCalendarView } from 'react-big-calendar';
import * as moment from 'moment-timezone';
import { observer } from 'mobx-react';
import { DateTime } from 'luxon';
import useStore from '../../../store/useStore';
import { LeadTypeAbbr } from '../../../constants';
import { numberFormatter } from '../../../util/formatters';
import { CalendarLeadSimple } from '../../../types';
import {
    LeadStatsMap,
    LeadViewCalendarEvent,
    LeadViewReducerAction,
    LeadViewState,
    LeadViewStats,
    selectCalendarLeads
} from './LeadView.helpers';
import { AppCard, AppCardGrid, StatCard, useSnackbar } from '../../../components-v2/shared';
import { useFilters } from '../../Filters/Filters';
import { DateRangePreset, InitialFilters } from '../../Filters/Filters.helpers';
import { StringParam, useQueryParam } from 'use-query-params';
import { handlePromise } from '@lambdacurry/component-library';
import { DashboardData } from '../../Dashboard/HomeDashboard.helpers';
import { CircularProgress } from '@material-ui/core';

import { getRouteWithContext } from '../../../routes/routeUtils';

import './lead-view-calendar.scss';

const getCalendarDateInTimezone = (date: Date, timezone: string) =>
    DateTime.fromJSDate(date).setZone(timezone, { keepLocalTime: true });

const getMonthDateRange = (date: Date, timezone: string) => {
    const calendarDate = getCalendarDateInTimezone(date, timezone);
    const monthStart = calendarDate.startOf('month');
    const monthEnd = calendarDate.endOf('month');
    const startOffset = monthStart.weekday % 7;
    const endOffset = 6 - (monthEnd.weekday % 7);

    return {
        start: monthStart.minus({ days: startOffset }).startOf('day').toJSDate(),
        end: monthEnd.plus({ days: endOffset }).endOf('day').toJSDate()
    };
};

const getWeekDateRange = (date: Date, timezone: string) => {
    const currentDate = getCalendarDateInTimezone(date, timezone);
    const startOffset = currentDate.weekday % 7;
    const start = currentDate.minus({ days: startOffset }).startOf('day');

    return {
        start: start.toJSDate(),
        end: start.plus({ days: 6 }).endOf('day').toJSDate()
    };
};

const getDayDateRange = (date: Date, timezone: string) => {
    const currentDate = getCalendarDateInTimezone(date, timezone);

    return {
        start: currentDate.startOf('day').toJSDate(),
        end: currentDate.endOf('day').toJSDate()
    };
};

const isCalendarView = (view?: string | null): view is BigCalendarView =>
    view === 'month' || view === 'week' || view === 'day';

type CalendarLeadsResponse = {
    company_count: number;
    count: number;
    limit: number;
    offset: number;
    leads: CalendarLeadSimple[];
};

export const LeadViewCalendar: React.FC<{
    state: LeadViewState;
    dispatch: React.Dispatch<LeadViewReducerAction>;
}> = observer(({ state, dispatch }) => {
    const { store, agencyStore } = useStore();
    const { activeCompanyId, Api, router, filterCompanies } = store;
    const { activeAgencyId } = agencyStore;
    const { addSnackbar } = useSnackbar();
    const { filters } = useFilters();
    const [calendarView, setCalendarView] = useQueryParam('calendarView', StringParam);
    const [activeCalendarView, setActiveCalendarView] = React.useState<BigCalendarView>(
        isCalendarView(calendarView) ? calendarView : 'month'
    );
    const [calendarDate, setCalendarDate] = React.useState(() => new Date());
    const latestRequestId = React.useRef(0);
    const lastStartedRequestKey = React.useRef<string | undefined>();
    const lastHandledCalendarView = React.useRef(calendarView);
    const filtersTimezone = filters?.dateRange.timezone;

    React.useEffect(() => {
        if (
            calendarView !== lastHandledCalendarView.current &&
            isCalendarView(calendarView) &&
            calendarView !== activeCalendarView
        ) {
            setActiveCalendarView(calendarView);
        }
        lastHandledCalendarView.current = calendarView;
    }, [activeCalendarView, calendarView]);

    const includeTestCompanies = React.useMemo(() => {
        if (!activeCompanyId) {
            return false;
        }

        const enableTestCompanyCheck = filterCompanies.find((co: { id: number }) => co.id === activeCompanyId);
        return enableTestCompanyCheck?.is_test || false;
    }, [activeCompanyId, filterCompanies]);

    const calendarDateRange = React.useMemo(() => {
        const timezone = filtersTimezone || InitialFilters.dateRange.timezone;

        if (activeCalendarView === 'day') {
            return getDayDateRange(calendarDate, timezone);
        }

        if (activeCalendarView === 'week') {
            return getWeekDateRange(calendarDate, timezone);
        }

        return getMonthDateRange(calendarDate, timezone);
    }, [activeCalendarView, calendarDate, filtersTimezone]);

    const requestFilters = React.useMemo(
        () => {
            if (!filters) {
                return undefined;
            }

            return {
                ...InitialFilters,
                ...filters,
                dateRange: {
                    preset: DateRangePreset.CUSTOM,
                    timezone: filters.dateRange.timezone || InitialFilters.dateRange.timezone,
                    custom: calendarDateRange
                },
                include_test_companies: includeTestCompanies || filters.include_test_companies || false
            };
        },
        [calendarDateRange, filters, includeTestCompanies]
    );

    const requestKey = React.useMemo(() => {
        if (!requestFilters) {
            return undefined;
        }

        return JSON.stringify(requestFilters);
    }, [requestFilters]);

    const fetchCalendarData = React.useCallback(async (requestId: number, nextRequestKey?: string) => {
        if (!requestFilters) {
            if (requestId === latestRequestId.current) {
                dispatch({ name: 'setCalendarData', payload: undefined });
                dispatch({ name: 'setCalendarRequestKey', payload: undefined });
            }
            return;
        }

        if (!requestFilters.dateRange.custom?.start || !requestFilters.dateRange.custom.end) {
            if (requestId === latestRequestId.current) {
                dispatch({ name: 'setCalendarData', payload: undefined });
                dispatch({ name: 'setCalendarRequestKey', payload: undefined });
            }
            return;
        }

        const pageSize = 500;
        let offset = 0;
        let totalCount = 0;
        let companyCount = 0;
        const allLeads: CalendarLeadSimple[] = [];

        while (true) {
            if (requestId !== latestRequestId.current) {
                return;
            }

            const [response, error] = await handlePromise<{ data: CalendarLeadsResponse }>(
                Api.client.post('/leads/list', {
                    filters: requestFilters,
                    context: 'calendar',
                    limit: pageSize,
                    offset
                })
            );

            if (!response || error) {
                if (requestId === latestRequestId.current) {
                    dispatch({ name: 'setCalendarData', payload: undefined });
                    dispatch({ name: 'setCalendarRequestKey', payload: undefined });
                    addSnackbar('Failed to fetch calendar data.', { variant: 'error' });
                }
                return;
            }

            const { count, company_count, leads = [] } = response.data;
            totalCount = count;
            companyCount = company_count;
            allLeads.push(...leads);

            if (!leads.length || allLeads.length >= count) {
                if (requestId !== latestRequestId.current) {
                    return;
                }

                dispatch({
                    name: 'setCalendarData',
                    payload: {
                        ...response.data,
                        count: totalCount,
                        company_count: companyCount,
                        offset: 0,
                        limit: allLeads.length,
                        leads: allLeads
                    }
                });
                dispatch({ name: 'setCalendarRequestKey', payload: nextRequestKey });
                return;
            }

            offset += pageSize;
        }
    }, [Api.client, addSnackbar, dispatch, requestFilters]);

    // Re-fetch analytics data whenever the filters, context, activeCompanyId, or activeAgencyId changes.
    const fetchAnalyticsData = React.useCallback(async (requestId: number) => {
        if (!requestFilters) {
            dispatch({ name: 'setLeadViewStats', payload: undefined });
            return;
        }

        const [response, error] = await handlePromise<{ data: DashboardData }>(
            Api.client.post('/analytics/dashboard', { filter: requestFilters })
        );

        if (requestId !== latestRequestId.current) {
            return;
        }

        if (!response?.data || error) {
            dispatch({ name: 'setLeadViewStats', payload: undefined });
            return console.error('error: ', error);
        }

        const { totals } = response.data;
        const statsData: LeadViewStats = {
            leads: totals.leads,
            tours: totals.leadsByModule.SG || 0,
            chats: totals.leadsByModule.CH || 0,
            livechat: totals.leadsByModule.LC || 0,
            offers: totals.leadsByModule.CM || 0
        };

        dispatch({ name: 'setLeadViewStats', payload: statsData });
    }, [Api.client, dispatch, requestFilters]);

    React.useEffect(() => {
        const isNewRequest = state.calendarRequestKey !== requestKey;

        if (!isNewRequest && lastStartedRequestKey.current === requestKey) {
            return;
        }

        lastStartedRequestKey.current = requestKey;
        const requestId = ++latestRequestId.current;

        if (isNewRequest) {
            dispatch({ name: 'setCalendarData', payload: undefined });
            dispatch({ name: 'setLeadViewStats', payload: undefined });
            dispatch({ name: 'setCalendarRequestKey', payload: undefined });
            dispatch({ name: 'setIsLoading', payload: true });
        }

        Promise.allSettled([
            fetchCalendarData(requestId, requestKey),
            fetchAnalyticsData(requestId)
        ]).then(() => {
            if (isNewRequest && requestId === latestRequestId.current) {
                dispatch({ name: 'setIsLoading', payload: false });
            }
        });
    }, [
        activeAgencyId,
        activeCompanyId,
        dispatch,
        fetchAnalyticsData,
        fetchCalendarData,
        filterCompanies,
        requestKey,
        state.calendarRequestKey
    ]);

    const localizer = BigCalendar.momentLocalizer(moment);

    const eventPropGetter = (event: LeadViewCalendarEvent) => ({
        className: `${LeadTypeAbbr[event.lead_type].toLowerCase()}-calendar-event view-${activeCalendarView}`
    });

    const handleEventClick = (event: LeadViewCalendarEvent, e: React.SyntheticEvent) => {
        const currentView = { ...router.currentView! };
        const leadDetailsRoute = getRouteWithContext('LeadDetailsByCustomer', router);

        router.goTo(
            leadDetailsRoute,
            {
                agencyId: agencyStore.activeAgencyId,
                companyId: store.activeCompanyId,
                customerId: event.lead_customer_id,
                id: event.id,
                backRoute: currentView
            },
            store
        );
    };

    const handleViewChange = (view: BigCalendarView) => {
        lastHandledCalendarView.current = view;
        setActiveCalendarView(view);
        setCalendarView(view, 'replaceIn');
    };

    return (
        <>
            <AppCardGrid>
                <AppCard className="lead-view-calendar-card">
                    <BigCalendar
                        popup
                        date={calendarDate}
                        view={activeCalendarView}
                        onNavigate={date => setCalendarDate(date)}
                        onView={handleViewChange}
                        views={['month', 'week', 'day']}
                        localizer={localizer}
                        events={selectCalendarLeads(state)}
                        onSelectEvent={handleEventClick}
                        step={30}
                        eventPropGetter={eventPropGetter}
                        className={`lead-view-calendar lead-view-calendar-${activeCalendarView}`}
                    />
                </AppCard>

                {state.isLoading && <CircularProgress size="24px" />}

                {!state.isLoading && (
                    <>
                        {Object.keys(LeadStatsMap).map(key => {
                            const { icon, label } = LeadStatsMap[key];
                            if (!state.leadViewStats) {
                                return null;
                            }

                            return (
                                <StatCard
                                    key={label}
                                    label={label}
                                    value={numberFormatter(state.leadViewStats[key])}
                                    color="blue"
                                    icon={icon}
                                />
                            );
                        })}
                    </>
                )}
            </AppCardGrid>
        </>
    );
});
